// Attach the signed-in person's Firebase ID token to every call to the Codenec
// backend, as `Authorization: Bearer <token>`.
//
// The backend now identifies the caller ONLY from a verified token (see
// backend/src/middleware/auth.js). It used to trust a uid sent in the request,
// which let anyone open someone else's billing portal or edit their progress.
//
// Doing this in one interceptor, instead of in each service function, means no
// call can forget it, and every call gets a FRESH token: getIdToken() returns
// the cached one while it is valid and silently refreshes it after its hour is
// up (the `accessToken` snapshot on the user object never refreshes — see
// utils/authToken.js).
//
// Scope: only requests whose URL starts with REACT_APP_API_BASE_URL, and never
// when the request already carries its own Authorization header (the Studio
// service sets one). No signed-in user means no header; public routes such as
// /contact and /get-started still work without one.
//
// Imported once, for its side effect, from src/index.js.
import axios from "axios";
import { auth } from "../config/firebase-config";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "";

const isBackendRequest = (url) =>
  !!API_BASE_URL && typeof url === "string" && url.startsWith(API_BASE_URL);

const hasAuthorizationHeader = (headers) => {
  if (!headers) return false;
  if (typeof headers.has === "function") return headers.has("Authorization");
  return Object.keys(headers).some((key) => key.toLowerCase() === "authorization");
};

const setAuthorizationHeader = (config, value) => {
  if (config.headers && typeof config.headers.set === "function") {
    config.headers.set("Authorization", value);
  } else {
    config.headers = { ...(config.headers || {}), Authorization: value };
  }
};

axios.interceptors.request.use(async (config) => {
  if (!isBackendRequest(config.url) || hasAuthorizationHeader(config.headers)) {
    return config;
  }

  const user = auth.currentUser;
  if (!user) return config;

  try {
    const token = await user.getIdToken();
    if (token) setAuthorizationHeader(config, `Bearer ${token}`);
  } catch (error) {
    // Offline or mid sign-out: send the request without a token and let the
    // backend answer 401, which the caller already handles as a failure.
    console.error("Could not get a Firebase ID token for the request:", error);
  }

  return config;
});
